/**
 * How to install each browser extension, in a panel that slides in from the
 * right. The steps used to sit open on the network page, above the devices
 * and tokens, and pushed them off screen.
 *
 * There are three builds: the connected and the queued extension for Chrome,
 * and the queued one for Firefox (there is no Firefox build of the connected
 * one). The panel opens on the one whose link was clicked and has a tab for
 * each, so someone who picked the wrong one can switch.
 */
import { useCallback, useState, type ReactNode } from 'react';
import Drawer from '../common/Drawer';
import { SubTabs } from '../common/Tabs';
import ExtensionDownload from './ExtensionDownload';
import ServerAddress from './ServerAddress';

export type InstallStyle = 'chrome-connected' | 'chrome-queued' | 'firefox-queued';

const STYLES: { key: InstallStyle; label: string }[] = [
  { key: 'chrome-connected', label: 'Chrome: connected' },
  { key: 'chrome-queued', label: 'Chrome: queued' },
  { key: 'firefox-queued', label: 'Firefox: queued' },
];

const LINK = 'text-sm text-blue-600 dark:text-blue-400 hover:underline';

/** The link that opens the guide. */
export function InstallGuideLink({ build, children = 'How to install' }: { build: InstallStyle; children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={LINK} data-testid={`install-guide-${build}`}>
        {children}
      </button>
      {open && <InstallGuide initial={build} onClose={close} />}
    </>
  );
}

export default function InstallGuide({ initial, onClose }: { initial: InstallStyle; onClose: () => void }) {
  const [style, setStyle] = useState<InstallStyle>(initial);
  return (
    <Drawer
      onClose={onClose}
      width="w-[36rem]"
      eyebrow="Browser extension"
      title={<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">How to install</h2>}
    >
      <div className="space-y-5 text-sm text-gray-700 dark:text-gray-300">
        <SubTabs className="" tabs={STYLES} active={style} onChange={setStyle} />
        <Which />
        {style === 'chrome-connected' && <ChromeSteps kind="connected" />}
        {style === 'chrome-queued' && <ChromeSteps kind="queued" />}
        {style === 'firefox-queued' && <FirefoxSteps />}
        <Console firefox={style === 'firefox-queued'} />
        <Trouble />
      </div>
    </Drawer>
  );
}

function H({ children }: { children: ReactNode }) {
  return <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1.5">{children}</h3>;
}

function B({ children }: { children: ReactNode }) {
  return <span className="font-medium text-gray-900 dark:text-gray-100">{children}</span>;
}

function Code({ children }: { children: ReactNode }) {
  return <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">{children}</code>;
}

function Steps({ children }: { children: ReactNode }) {
  return <ol className="list-decimal pl-5 space-y-2">{children}</ol>;
}

function Which() {
  return (
    <div>
      <H>Which one?</H>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <B>Connected</B> (green dot on its icon) keeps a live connection, so a search or page your
          research needs is fetched straight away. Use it while you are working.
        </li>
        <li>
          <B>Queued</B> (orange dot) checks in every 30 seconds and works through what is waiting, even
          overnight while you are away.
        </li>
      </ul>
      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
        Both can run in the same Chrome, and one access token works in both.
      </p>
    </div>
  );
}

/** Token and server address: the same for every build. */
function SignIn({ start }: { start: number }) {
  return (
    <ol start={start} className="list-decimal pl-5 space-y-2">
      <li>
        Make an access token: close this panel, go to <B>Access tokens</B> on this page, type a name
        (for example "Home laptop") and press <B>Generate token</B>. Copy it; it is shown only once.
      </li>
      <li>
        Click the extension's icon in the toolbar. Paste the token into <B>Access token</B>. Check that{' '}
        <B>Server address</B> matches the one below (it is filled in already).
        <div className="mt-2">
          <ServerAddress />
        </div>
      </li>
      <li>
        Press <B>Go online</B>. If the popup shows <B>Allow access to all sites</B>, press it: the
        extension can't open pages until you do.
      </li>
      <li>
        Your browser shows up under <B>Devices</B> on this page with a green dot while it is online.
      </li>
    </ol>
  );
}

function ChromeSteps({ kind }: { kind: 'connected' | 'queued' }) {
  return (
    <>
      <div>
        <H>Install</H>
        <Steps>
          <li>
            Download the extension and unzip it. You get a folder. Keep it somewhere it won't be
            deleted: Chrome runs the extension from that folder.
            <div className="mt-2">
              <ExtensionDownload kind={kind} admin={false} />
            </div>
          </li>
          <li>
            Open <Code>chrome://extensions</Code> in Chrome and switch on <B>Developer mode</B> (top
            right).
          </li>
          <li>
            Press <B>Load unpacked</B> and pick the unzipped folder.
          </li>
          <li>Pin it: click the puzzle icon in the toolbar, then the pin next to the extension.</li>
        </Steps>
      </div>
      <div>
        <H>Connect it to your account</H>
        <SignIn start={5} />
      </div>
      <div>
        <H>Updating to a new version</H>
        <p>
          Download the new zip and unzip it over the same folder, replacing the old files. Then press
          the reload arrow on the extension's card in <Code>chrome://extensions</Code>. Your token and
          settings are kept.
        </p>
      </div>
    </>
  );
}

function FirefoxSteps() {
  return (
    <>
      <div>
        <H>Install</H>
        <p className="mb-2">
          Firefox only keeps extensions that Mozilla has signed. Ask an admin which file you have:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <B>A signed .xpi file:</B> open it in Firefox (drag it onto a Firefox window) and press{' '}
            <B>Add</B>. It stays installed.
          </li>
          <li>
            <B>An unsigned .zip:</B> unzip it, open <Code>about:debugging</Code>, choose{' '}
            <B>This Firefox</B>, press <B>Load Temporary Add-on</B> and pick the folder's{' '}
            <Code>manifest.json</Code>. Firefox removes it when it restarts, so you load it again each
            time.
          </li>
        </ul>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Admins: build it on GitHub with Actions → "Package queued extension (Firefox)". With the
          Mozilla keys set up, the run also makes the signed .xpi.
        </p>
      </div>
      <div>
        <H>Connect it to your account</H>
        <SignIn start={1} />
      </div>
    </>
  );
}

function Console({ firefox }: { firefox: boolean }) {
  return (
    <div>
      <H>The console: signed-in sites</H>
      <p className="mb-2">
        Recon can search sites you have to be signed in to, like X, LinkedIn, Facebook and Reddit,
        using your own accounts. Nothing is sent until you say so.
      </p>
      <Steps>
        <li>
          In the extension's popup, press <B>Open console</B>. It opens in the{' '}
          {firefox ? 'sidebar' : 'side panel'}.
        </li>
        <li>Switch on the sites you want and sign in to each one in the tab the console opens.</li>
        <li>
          When a recon wants a search, it waits in the console. Press <B>Start</B>: each search opens in
          its site's tab. Read it, then press <B>Ingest</B> on the bar at the top of the page to send
          it, or <B>Skip</B>.
        </li>
        <li>
          Under <B>Ready to read</B>, <B>Process now</B> has the room read what you sent straight away.
          Otherwise its next dig reads it.
        </li>
      </Steps>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        The console spaces searches out and keeps to an hourly and daily limit for each site, so your
        accounts don't get flagged. The tabs it opens go in a tab group called "Lumnian".
      </p>
    </div>
  );
}

function Trouble() {
  return (
    <div>
      <H>If it doesn't work</H>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>
          <B>The device stays grey:</B> press <B>Test connection</B> in the popup. It shows{' '}
          <B>Connected ✓</B> or the error. An error usually means the token is wrong or revoked, or
          the server address is wrong.
        </li>
        <li>
          <B>Nothing gets fetched:</B> check the popup for <B>Allow access to all sites</B>, and that it
          says it is online.
        </li>
        <li>
          <B>After an update nothing changed:</B> press the reload arrow on the extension in{' '}
          <Code>chrome://extensions</Code>.
        </li>
      </ul>
    </div>
  );
}
